import type { ElevationSeries } from '@shared/model/profile';
import { useElementWidth } from '../hooks/useElementWidth';
import styles from './ElevationChart.module.css';

interface Props {
    series: ElevationSeries[];
    /** Longest itinerary of the page (metres), so that every chart shares the same x scale. */
    maxDistance: number;
    /** Altitude range of the page. */
    minAlt: number;
    maxAlt: number;
    height?: number;
    /** Shown while the profiles are loading. */
    loading?: boolean;
}

const RIGHT_AXIS_WIDTH = 60;
const TOP = 4;
const BOTTOM = 14;
const ALT_MARGIN = 1.1;
const X_TICK = 1000;
const Y_TICK = 200;

/** Lightweight SVG replacement of the Highcharts profile of the historical site. */
export function ElevationChart({ series, maxDistance, minAlt, maxAlt, height = 70, loading = false }: Props) {
    const [ref, width] = useElementWidth<HTMLDivElement>();
    const plotWidth = Math.max(0, width - RIGHT_AXIS_WIDTH);
    const plotHeight = height - TOP - BOTTOM;
    const yMax = maxAlt * ALT_MARGIN;
    const yMin = Math.min(minAlt, yMax - 1);
    const x = (d: number) => (maxDistance > 0 ? (d / maxDistance) * plotWidth : 0);
    const y = (z: number) => TOP + plotHeight - ((z - yMin) / (yMax - yMin)) * plotHeight;

    // One label per km, or every 2 km (then 5 km) when they would overlap on narrow screens.
    const kmWidth = maxDistance > 0 ? (X_TICK / maxDistance) * plotWidth : plotWidth;
    const labelEvery = kmWidth >= 36 ? 1 : kmWidth >= 18 ? 2 : 5;
    const xTicks: number[] = [];
    for (let d = X_TICK; d <= maxDistance; d += X_TICK) xTicks.push(d);
    const yTicks: number[] = [];
    for (let z = Math.ceil(yMin / Y_TICK) * Y_TICK; z <= yMax; z += Y_TICK) yTicks.push(z);

    return (
        <div ref={ref} className={styles.chart} style={{ height }} aria-hidden="true">
            {width > 0 && (
                <svg width={width} height={height} className={loading ? styles.loading : undefined}>
                    {yTicks.map((z) => (
                        <g key={z}>
                            <line x1={0} x2={plotWidth} y1={y(z)} y2={y(z)} className={styles.grid} />
                            <text x={plotWidth + 4} y={y(z)} className={styles.yLabel} dominantBaseline="middle">
                                {z} m
                            </text>
                        </g>
                    ))}
                    <line x1={0} x2={plotWidth} y1={TOP + plotHeight} y2={TOP + plotHeight} className={styles.axis} />
                    {xTicks.map((d) => (
                        <g key={d}>
                            <line
                                x1={x(d)}
                                x2={x(d)}
                                y1={TOP + plotHeight}
                                y2={TOP + plotHeight - 5}
                                className={styles.axis}
                            />
                            {(d / X_TICK) % labelEvery === 0 && (
                                <text x={x(d)} y={TOP + plotHeight - 7} textAnchor="middle" className={styles.xLabel}>
                                    {d / 1000} km
                                </text>
                            )}
                        </g>
                    ))}
                    {series.map((s) =>
                        s.points.length > 1 ? (
                            <polyline
                                key={s.code}
                                points={s.points.map(([d, z]) => `${x(d).toFixed(1)},${y(z).toFixed(1)}`).join(' ')}
                                fill="none"
                                stroke={s.color}
                                strokeWidth={2}
                                strokeLinejoin="round"
                            />
                        ) : null,
                    )}
                    <text
                        x={width - 4}
                        y={TOP + plotHeight / 2}
                        className={styles.yTitle}
                        textAnchor="middle"
                        transform={`rotate(90 ${width - 4} ${TOP + plotHeight / 2})`}
                    >
                        Altitude
                    </text>
                </svg>
            )}
        </div>
    );
}
